import { MutableState } from '../State';
import {
  getSuperLollipopWinStreakRequired,
  isSuperLollipopAvailable,
} from './superLollipop.getters';

export function increaseSuperLollipopWinStreak(state: MutableState): void {
  // Increase only if super lollipop feature is available for the user
  if (!isSuperLollipopAvailable(state)) return;

  // Initialize super lollipop object if it doesn't exist
  if (!state.superLollipop) {
    state.superLollipop = {
      winStreak: 0,
      activationCount: 0,
    };
  }

  // Increase the super lollipop win streak
  // Can be higher than the win streak required to activate the feature, for analytics purposes
  state.superLollipop.winStreak += 1;

  // Every time the win streak reaches the required amount, increase the number of activations
  if (
    state.superLollipop.winStreak === getSuperLollipopWinStreakRequired(state)
  ) {
    state.superLollipop.activationCount += 1;
  }

  // Clear the lost super lollipop flag after the win streak is increased
  state.superLollipop.lostSuperLollipopLastMatch = false;
  state.superLollipop.lostSuperLollipopThisLevel = false;
}

export function clearSuperLollipopWinStreak(state: MutableState): void {
  // Skip if super lollipop feature is not available for the user
  if (!isSuperLollipopAvailable(state)) return;

  // Skip if super lollipop object doesn't exist
  if (!state.superLollipop) return;

  // Mark super lollipop as lost if it was active
  const wasActive =
    state.superLollipop.winStreak >= getSuperLollipopWinStreakRequired(state);
  state.superLollipop.lostSuperLollipopLastMatch = wasActive;

  if (wasActive) {
    state.superLollipop.lostSuperLollipopThisLevel = true;
  }

  // Reset the super lollipop win streak
  state.superLollipop.winStreak = 0;
}

export function updateSuperLollipopLastSeen(
  state: MutableState,
  timestamp: number,
): void {
  // Increase only if super lollipop feature is available for the user
  if (!isSuperLollipopAvailable(state)) return;

  // Initialize super lollipop object if it doesn't exist
  if (!state.superLollipop) {
    state.superLollipop = {
      winStreak: 0,
      activationCount: 0,
    };
  }

  // Increase the super lollipop win streak
  state.superLollipop.lastSeenAt = timestamp;
}

/**
 * Auto activate super lollipop if not active yet
 */
export function activateSuperLollipop(state: MutableState): void {
  // Increase only if super lollipop feature is available for the user
  if (!isSuperLollipopAvailable(state)) return;

  // Initialize super lollipop object if it doesn't exist
  if (!state.superLollipop) {
    state.superLollipop = {
      winStreak: 0,
      activationCount: 0,
    };
  }

  const winStreak = getSuperLollipopWinStreakRequired(state);

  // Skip if already activated
  if (state.superLollipop.winStreak >= winStreak) {
    return;
  }

  // Increase the super lollipop activation count
  state.superLollipop.winStreak = winStreak;
  state.superLollipop.activationCount += 1;
  state.superLollipop.lostSuperLollipopLastMatch = false;
  state.superLollipop.lostSuperLollipopThisLevel = false;
}
